import { ChevronRightIcon } from '@heroicons/react/20/solid'
import { forwardRef, useEffect, useState, useRef, useCallback } from 'react'
import classNames from 'classnames'
import { Menu, MenuButton, MenuItem } from '@szhsin/react-menu'
import { useDroppable, useDraggable } from '@dnd-kit/core'
import { NoteType, isTeamspaceNote, isPrivateNote } from '../../utils/note'
import { useDeleteNote } from '../../hooks/useDeleteNote'
import { useMoveToTrash } from '../../hooks/useMoveToTrash'
import { useMoveToArchive } from '../../hooks/useMoveToArchive'
import useSaveNoteTitle from '../../hooks/useSaveNoteTitle'
import { useLeaveTeamspace } from '../../hooks/useLeaveTeamspace'
import { useNotification } from '../../providers/NotificationProvider'
import useMoveNote from '../../hooks/useMoveNote'
import { errorIcon } from '../../components/Notification'
import { trackError } from '../../lib/analytics'
import { useSidebarProvider } from '../../contexts/Sidebar'
import { useSelectedRecordName } from '../../contexts/SelectedRecordName'
import { type SidebarEntry } from './SidebarBuilder'
import { useIsMobile } from '../../hooks/useIsMobile'

const LOCAL_STORAGE_PREFIX = 'SIDEBAR_ITEM_OPEN_STATE_'

const getLocalStorageKey = (itemName: string) => {
  return `${LOCAL_STORAGE_PREFIX}${itemName}`
}

function setItemStateToLocalStorage(itemName: string, state: boolean) {
  if (state) {
    localStorage.setItem(getLocalStorageKey(itemName), JSON.stringify(state))
  } else {
    localStorage.removeItem(getLocalStorageKey(itemName))
  }
}

function getItemStateFromLocalStorage(itemName: string): boolean | undefined {
  const savedState = localStorage.getItem(getLocalStorageKey(itemName))
  return savedState == null ? undefined : (JSON.parse(savedState) as boolean)
}

function SkeletonLoader() {
  const lineLengths = [80, 60]
  return (
    <li className='ml-5'>
      <div className='skeleton my-3 h-3 rounded-md' style={{ width: `60%` }} />
      {lineLengths.map((length, index) => {
        return (
          <div
            key={index}
            className='skeleton my-3 ml-4 h-3 rounded-md'
            style={{ width: `${length.toString()}%` }}
          />
        )
      })}
      <div
        className='skeleton mb-3 mt-6 h-3 rounded-md'
        style={{ width: `70%` }}
      />
      <div
        className='skeleton my-3 ml-4 h-3 rounded-md'
        style={{ width: `50%` }}
      />
      <div
        className='skeleton my-3 ml-4 h-3 rounded-md'
        style={{ width: `40%` }}
      />
    </li>
  )
}

function traverse(entries: SidebarEntry[] | undefined): string[] {
  if (!entries) return [] as string[]

  let recordNames: string[] = []

  for (const entry of entries) {
    recordNames.push(entry.recordName)

    if (entry.children) {
      recordNames = [...recordNames, ...traverse(entry.children)]
    }
  }

  return recordNames
}

type SidebarItemProps = {
  item: SidebarEntry
  onManageTeamspace: (_id: string, _title: string) => void
  collapsed?: boolean
  toggleSidebar: () => void
  isDragging?: boolean
}

// This is the presentational component that will be used both directly and by the draggable version
const SidebarItemContent = forwardRef<
  HTMLLIElement,
  SidebarItemProps & {
    isOver?: boolean
    attributes?: Record<string, any>
    listeners?: Record<string, any>
    touchAction?: Record<string, string>
    droppableRef?: (node: HTMLDivElement | null) => void
  }
>(
  (
    {
      item,
      onManageTeamspace,
      collapsed,
      isOver,
      attributes,
      listeners,
      touchAction,
      droppableRef,
      toggleSidebar,
      isDragging,
    },
    reference
  ) => {
    const selectedRecordName = useSelectedRecordName()
    const {
      handleSelectRecordName,
      createFolder,
      createNote,
      handleExportSupabaseTeamspace,
      privateProjectNotes,
    } = useSidebarProvider()
    const moveNote = useMoveNote()
    const isMobile = useIsMobile()
    const touchStartTime = useRef<number>(0)

    // #region open state
    const [isOpen, setIsOpen] = useState<boolean>(getInitialState())

    // Fetch the saved state from localStorage for the current item or fall back to its default
    function getInitialState(): boolean {
      if (collapsed) {
        return false
      }

      const savedState = getItemStateFromLocalStorage(item.recordName)

      if (savedState === undefined) {
        return Boolean(item.header)
      }
      return savedState
    }

    useEffect(() => {
      setItemStateToLocalStorage(item.recordName, isOpen)
    }, [isOpen, item.recordName])

    const hasRightChevron = item.header || item.noteType === NoteType.TEAM_SPACE
    // #endregion

    // #region teamspace
    const leaveTeamspace = useLeaveTeamspace()
    // #endregion

    // #region editing
    const [editedTitle, setEditedTitle] = useState<string | undefined>(
      undefined
    )
    const saveNoteTitle = useSaveNoteTitle()
    function saveTitle() {
      if (item.noteType) {
        saveNoteTitle.mutate({
          recordName: item.recordName,
          noteType: item.noteType,
          title: editedTitle ?? '',
          children: item.children,
        })
      }
      setEditedTitle(undefined)
    }
    const deleteNote = useDeleteNote()
    const { moveToTrash } = useMoveToTrash()
    const { moveToArchive } = useMoveToArchive()
    const { showNotification } = useNotification()

    // Helper function to handle delete/move to trash logic
    const handleDelete = useCallback(
      async (descendenceIds: string[] = []) => {
        if (!item.noteType) return

        const isPrivate = isPrivateNote(item.noteType)
        const isFolder = Boolean(item.children)

        // Determine action and confirmation message
        const action = isPrivate ? 'move to trash' : 'permanently delete'
        const confirmMessage = isFolder
          ? `Are you sure you wish to ${action} this folder and its content?`
          : `Are you sure you wish to ${action} this note?`

        if (!window.confirm(confirmMessage)) return

        try {
          if (isPrivate) {
            // Move private notes/folders to trash
            await moveToTrash({
              recordName: item.recordName,
              noteType: item.noteType,
              children: item.children,
              descendenceIds,
            })
          } else {
            // Permanently delete team notes/folders
            deleteNote.mutate({
              recordName: item.recordName,
              noteType: item.noteType,
              descendenceIds,
            })
          }
        } catch (error) {
          // Track error for analytics
          trackError('sidebarItem', 'Error during delete/move to trash', {
            recordName: item.recordName,
            noteType: item.noteType,
            isPrivate,
            error: error instanceof Error ? error.message : 'Unknown error',
          })

          // Show user-friendly error notification
          const errorMessage =
            error instanceof Error ? error.message : 'Unknown error occurred'
          showNotification({
            title: 'Error',
            message: errorMessage,
            icon: errorIcon,
          })
        }
      },
      [item, moveToTrash, deleteNote, showNotification]
    )

    // Helper function to handle archive logic
    const handleArchive = useCallback(
      async (descendenceIds: string[] = []) => {
        if (!item.noteType) return

        const isPrivate = isPrivateNote(item.noteType)

        // Only allow archiving private notes
        if (!isPrivate) {
          console.warn('Archive is only available for private notes')
          return
        }

        try {
          await moveToArchive({
            recordName: item.recordName,
            noteType: item.noteType,
            children: item.children,
            descendenceIds,
          })
        } catch (error) {
          // Track error for analytics
          trackError('sidebarItem', 'Error during archive', {
            recordName: item.recordName,
            noteType: item.noteType,
            error: error instanceof Error ? error.message : 'Unknown error',
          })

          // Show user-friendly error notification
          const errorMessage =
            error instanceof Error ? error.message : 'Unknown error occurred'
          showNotification({
            title: 'Error',
            message: errorMessage,
            icon: errorIcon,
          })
        }
      },
      [item, moveToArchive, showNotification]
    )

    // Helper function to handle restore
    const handleRestore = useCallback(async () => {
      if (!item.noteType) return

      try {
        await moveNote.mutateAsync({
          recordName: item.recordName,
          noteType: item.noteType,
          parent: undefined, // Move to root by setting parent to undefined
          children: item.children ?? [],
          privateProjectNotes: privateProjectNotes ?? new Map(),
          teamProjectNotes: new Map(),
        })

        showNotification({
          title: 'Restored',
          message: `${item.title} has been restored`,
          icon: 'far fa-check-circle',
        })
      } catch (error) {
        trackError('sidebarItem', 'Error during restore', {
          recordName: item.recordName,
          noteType: item.noteType,
          error: error instanceof Error ? error.message : 'Unknown error',
        })

        const errorMessage =
          error instanceof Error ? error.message : 'Unknown error occurred'
        showNotification({
          title: 'Error',
          message: errorMessage,
          icon: errorIcon,
        })
      }
    }, [item, moveNote, privateProjectNotes, showNotification])
    // #endregion

    // adjust width for narrower icons
    const iconWidth = item.icon?.includes('clipboard') ? 'w-3' : 'w-4'

    return (
      <li
        id={item.recordName}
        className={classNames(
          { hidden: item.isHidden },
          { 'opacity-50': item.disabled }
        )}
        ref={reference}
        style={touchAction}
        {...listeners}
        {...attributes}
      >
        {item.children ? (
          <div ref={droppableRef}>
            <div
              onClick={() => {
                setIsOpen(!isOpen)
              }}
              className={classNames(
                'group cursor-default truncate',
                // Only apply hover styles when NOT dragging
                !isDragging &&
                  'hover:bg-stone-200 dark:hover:bg-stone-200/[0.1]',
                // 'flex items-center text-left',
                'ml-2 flex items-center pl-2 text-left',
                item.header
                  ? 'rounded-[0.4em] py-0.5 text-sm font-bold text-gray-400 md:text-xs dark:text-gray-400'
                  : 'font-regular my-0.5 rounded-md py-1 text-base text-gray-700 md:text-sm md:leading-tight dark:text-gray-100',
                // item.recordName == selectedRecordName && 'bg-stone-200 dark:bg-stone-200/[0.1]',
                // isOver && 'bg-blue-200 dark:bg-blue-200/[0.1]',
                // !hasRightChevron && item.children.length > 0 && '-ml-2'
                'w-[calc(100%-0.5rem)]',
                {
                  'bg-stone-200 dark:bg-stone-200/[0.1]':
                    item.recordName == selectedRecordName && !isDragging,
                  // Only show drag over state for folders when dragging
                  'bg-blue-200 dark:bg-blue-200/[0.1]':
                    isOver && Boolean(item.children) && isDragging,
                  // Disable pointer events on children when dragging to prevent hover state issues
                  '[&>*]:pointer-events-none': isDragging,
                }
              )}
            >
              {/* The chevron on the left (regular folders) */}
              {/* {!hasRightChevron && item.children.length > 0 && (
                  <ChevronRightIcon
                    className={classNames(open ? 'rotate-90 text-gray-500 dark:text-gray-200' : 'text-gray-500 dark:text-gray-200', 'h-4 w-4 shrink-0 cursor-pointer')}
                    aria-hidden="true"
                  />
                )} */}
              {!hasRightChevron && item.children.length > 0 ? (
                <ChevronRightIcon
                  className={classNames(
                    isOpen
                      ? 'rotate-90 text-gray-500 dark:text-gray-200'
                      : 'text-gray-500 dark:text-gray-200',
                    'h-4 w-4 shrink-0 cursor-pointer transition-transform duration-300 ease-in-out'
                  )}
                  aria-hidden='true'
                />
              ) : (
                <span
                  className={classNames(
                    item.children.length > 0 || item.header ? 'ml-1' : 'ml-4'
                  )}
                />
              )}

              {/* Add an icon */}
              {/* {!item.header && <i className={classNames('text-[0.9rem] mr-2 text-center w-4 cursor-default', item.color, item.icon)} {...listeners} {...attributes}></i>} */}
              {item.header ? (
                <span />
              ) : (
                <i
                  className={classNames(
                    'mr-2 cursor-default text-center text-base md:text-[0.9rem] md:leading-tight',
                    iconWidth,
                    item.color,
                    item.icon
                  )}
                  {...listeners}
                  {...attributes}
                />
              )}

              {editedTitle ? (
                <input
                  autoFocus
                  type='text'
                  className='font-regular m-0 mr-3 w-full rounded-none bg-gray-100 px-1 py-0.5 text-base text-gray-700 md:text-sm dark:bg-gray-800 dark:text-gray-200'
                  value={editedTitle}
                  onChange={(e) => {
                    setEditedTitle(e.target.value)
                  }}
                  onFocus={(e) => {
                    e.target.select()
                  }}
                  onBlur={() => {
                    saveTitle()
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault()
                      saveTitle()
                    }
                    // prevent the parent from opening / closing
                    if (e.key === ' ') {
                      e.stopPropagation()
                    }
                    if (e.key === 'Escape') {
                      setEditedTitle(undefined)
                    }
                  }}
                />
              ) : (
                <span
                  className='w-full truncate'
                  onClick={
                    item.action
                      ? (e) => {
                          if (item.action) {
                            item.action()
                          }
                          e.stopPropagation()
                        }
                      : undefined
                  }
                >
                  {item.title}
                  {item.isLocked && (
                    <i
                      className='far fa-lock ml-2 w-4 text-center text-gray-400 dark:text-gray-500'
                      aria-hidden='true'
                    />
                  )}
                </span>
              )}

              {/* The chevron on the right (headers and teamspaces) */}
              {hasRightChevron && (
                <ChevronRightIcon
                  className={classNames(
                    isOpen
                      ? 'rotate-90 text-gray-500 dark:text-gray-200'
                      : 'text-gray-500 dark:text-gray-200',
                    'h-4 w-4 shrink-0 transition-transform duration-300 ease-in-out'
                  )}
                  aria-hidden='true'
                />
              )}

              {/* Show Empty Trash menu for trash folder */}
              {item.filename === '@Trash' && (
                <Menu
                  align='end'
                  boundingBoxPadding='25px 0px 0px 50px'
                  menuButton={
                    <MenuButton
                      className='m-0 ml-auto p-0 opacity-0 transition-opacity group-hover:opacity-100'
                      onClick={(event) => {
                        event.stopPropagation()
                      }}
                    >
                      <i
                        className='fa-regular fas fa-ellipsis-vertical mx-3 text-base md:text-sm md:leading-tight'
                        aria-hidden='true'
                      />
                    </MenuButton>
                  }
                >
                  <MenuItem
                    key='empty-trash'
                    onClick={(event) => {
                      event.syntheticEvent.stopPropagation()

                      // Execute async deletion in the background
                      void (async () => {
                        // Confirm before emptying trash
                        if (
                          !window.confirm(
                            'Are you sure you want to permanently delete all items in the trash? This action cannot be undone.'
                          )
                        ) {
                          return
                        }

                        // Get all items in trash (all children of the trash folder)
                        const trashItems = item.children ?? []

                        if (trashItems.length === 0) {
                          showNotification({
                            title: 'Trash is empty',
                            message:
                              'There are no items in the trash to delete.',
                            icon: 'far fa-info-circle',
                          })
                          return
                        }

                        // Track successful and failed deletions
                        let successCount = 0
                        const failedItems: string[] = []

                        // Show progress notification
                        showNotification({
                          title: 'Emptying trash...',
                          message: `Deleting ${String(trashItems.length)} item${trashItems.length === 1 ? '' : 's'}...`,
                          icon: 'far fa-spinner fa-spin',
                        })

                        // Delete all items in trash sequentially to avoid race conditions
                        for (const trashItem of trashItems) {
                          if (trashItem.noteType) {
                            try {
                              // Get all descendant IDs for folders
                              const descendenceIds = traverse(
                                trashItem.children
                              )

                              // Use deleteNote.mutateAsync to wait for completion
                              await deleteNote.mutateAsync({
                                recordName: trashItem.recordName,
                                noteType: trashItem.noteType,
                                descendenceIds,
                              })

                              successCount++
                            } catch (error) {
                              // Track failed items
                              failedItems.push(trashItem.title || 'Untitled')

                              trackError(
                                'emptyTrash',
                                'Failed to delete item',
                                {
                                  recordName: trashItem.recordName,
                                  noteType: trashItem.noteType,
                                  error:
                                    error instanceof Error
                                      ? error.message
                                      : 'Unknown error',
                                }
                              )
                            }
                          }
                        }

                        // Show appropriate notification based on results
                        if (failedItems.length === 0) {
                          // All items deleted successfully
                          showNotification({
                            title: 'Trash emptied',
                            message: `Successfully deleted ${String(successCount)} item${successCount === 1 ? '' : 's'}.`,
                            icon: 'far fa-check-circle',
                          })
                        } else if (successCount === 0) {
                          // All deletions failed
                          showNotification({
                            title: 'Error',
                            message:
                              'Failed to delete any items. Please try again.',
                            icon: errorIcon,
                          })
                        } else {
                          // Partial success
                          showNotification({
                            title: 'Partially emptied',
                            message: `Deleted ${String(successCount)} of ${String(trashItems.length)} items. Failed: ${failedItems.join(', ')}`,
                            icon: 'far fa-exclamation-triangle',
                          })
                        }
                      })() // End of async function
                    }}
                  >
                    <i
                      className='far fa-trash mr-2 w-4 text-center'
                      aria-hidden='true'
                    />
                    Empty Trash
                  </MenuItem>
                </Menu>
              )}

              {!item.hideContext &&
                !item.header &&
                !editedTitle &&
                item.filename !== '@Trash' && (
                  <Menu
                    align='end'
                    boundingBoxPadding='25px 0px 0px 50px'
                    menuButton={
                      <MenuButton
                        className='m-0 ml-auto p-0 opacity-0 transition-opacity duration-0 group-hover:opacity-100'
                        onClick={(event) => {
                          event.stopPropagation()
                        }}
                      >
                        <i
                          className={classNames(
                            'fa-regular fas fa-ellipsis-vertical text-base md:text-sm md:leading-tight',
                            hasRightChevron ? 'group-hover:mx-3' : 'mx-3'
                          )}
                          aria-hidden='true'
                        />
                      </MenuButton>
                    }
                  >
                    <MenuItem
                      key='new-folder'
                      onClick={(event) => {
                        // If we stop propagating events here, it doesn't open the folder
                        if (isOpen) {
                          event.syntheticEvent.stopPropagation()
                        }
                        setIsOpen(true)

                        createFolder(
                          isTeamspaceNote(
                            item.noteType ?? NoteType.PROJECT_NOTE
                          )
                            ? NoteType.TEAM_SPACE_NOTE
                            : NoteType.PROJECT_NOTE,
                          item.recordName
                        )
                      }}
                    >
                      <i
                        className='far fa-folder mr-2 w-4 text-center'
                        aria-hidden='true'
                      />
                      New Folder
                    </MenuItem>
                    <MenuItem
                      key='new-note'
                      onClick={(event) => {
                        // If we stop propagating events here, it doesn't open the folder
                        if (isOpen) {
                          event.syntheticEvent.stopPropagation()
                        }
                        setIsOpen(true)
                        createNote(
                          item.childNoteType ?? item.noteType,
                          item.recordName,
                          item.templatesContext
                        )
                      }}
                    >
                      <i
                        className='far fa-file-lines mr-2 w-4 text-center'
                        aria-hidden='true'
                      />
                      {item.templatesContext ? 'New Template' : 'New Note'}
                    </MenuItem>

                    {item.filename !== '@Templates' &&
                    (item.noteType !== NoteType.TEAM_SPACE ||
                      item.hasAdminRights) ? (
                      <>
                        <MenuItem
                          key='rename'
                          // Disable 'Rename' when it's a teamspace and the user is not an owner and not an admin
                          onClick={(event) => {
                            event.syntheticEvent.stopPropagation()
                            setEditedTitle(item.title)
                          }}
                        >
                          <i
                            className='far fa-input-text mr-2 w-4 text-center'
                            aria-hidden='true'
                          />
                          Rename
                        </MenuItem>
                        {/* Restore option for items in Archive or Trash */}
                        {item.noteType &&
                          isPrivateNote(item.noteType) &&
                          (item.archiveContext || item.trashContext) && (
                            <MenuItem
                              key='restore'
                              onClick={(event) => {
                                event.syntheticEvent.stopPropagation()
                                void handleRestore()
                              }}
                            >
                              <i
                                className='far fa-undo mr-2 w-4 text-center'
                                aria-hidden='true'
                              />
                              Restore
                            </MenuItem>
                          )}

                        {/* Archive option - only for items NOT in Archive or Trash */}
                        {item.noteType &&
                          isPrivateNote(item.noteType) &&
                          !item.archiveContext &&
                          !item.trashContext && (
                            <MenuItem
                              key='archive'
                              onClick={(event) => {
                                event.syntheticEvent.stopPropagation()
                                // traverse the tree and get all descendence ids of this item
                                const descendenceIds: string[] = traverse(
                                  item.children
                                )
                                void handleArchive(descendenceIds)
                              }}
                            >
                              <i
                                className='far fa-archive mr-2 w-4 text-center'
                                aria-hidden='true'
                              />
                              Archive
                            </MenuItem>
                          )}

                        {/* Move to Trash - for items NOT in Trash (but can be in Archive) */}
                        {item.noteType &&
                          isPrivateNote(item.noteType) &&
                          !item.trashContext && (
                            <MenuItem
                              key='move-to-trash'
                              onClick={(event) => {
                                event.syntheticEvent.stopPropagation()
                                // traverse the tree and get all descendence ids of this item
                                const descendenceIds: string[] = traverse(
                                  item.children
                                )
                                void handleDelete(descendenceIds)
                              }}
                            >
                              <i
                                className='far fa-trash-can mr-2 w-4 text-center'
                                aria-hidden='true'
                              />
                              Move to Trash
                            </MenuItem>
                          )}

                        {/* Delete Permanently - only for items in Trash */}
                        {item.noteType && item.trashContext && (
                          <MenuItem
                            key='delete-permanently'
                            onClick={(event) => {
                              event.syntheticEvent.stopPropagation()
                              if (
                                !window.confirm(
                                  'Are you sure you want to permanently delete this item? This action cannot be undone.'
                                )
                              ) {
                                return
                              }
                              // traverse the tree and get all descendence ids of this item
                              const descendenceIds: string[] = traverse(
                                item.children
                              )
                              // For permanent deletion, we need to use deleteNote directly
                              if (item.noteType) {
                                deleteNote.mutate({
                                  recordName: item.recordName,
                                  noteType: item.noteType,
                                  descendenceIds,
                                })
                              }
                            }}
                          >
                            <i
                              className='far fa-trash mr-2 w-4 text-center'
                              aria-hidden='true'
                            />
                            Delete Permanently
                          </MenuItem>
                        )}

                        {/* Regular Delete for team notes */}
                        {item.noteType && !isPrivateNote(item.noteType) && (
                          <MenuItem
                            key='delete'
                            onClick={(event) => {
                              event.syntheticEvent.stopPropagation()
                              // traverse the tree and get all descendence ids of this item
                              const descendenceIds: string[] = traverse(
                                item.children
                              )
                              void handleDelete(descendenceIds)
                            }}
                          >
                            <i
                              className='far fa-trash mr-2 w-4 text-center'
                              aria-hidden='true'
                            />
                            Delete
                          </MenuItem>
                        )}
                      </>
                    ) : undefined}

                    {item.noteType === NoteType.TEAM_SPACE && (
                      <>
                        {item.hasAdminRights ? (
                          <>
                            <MenuItem
                              key='export'
                              // Disable 'Manage Members' when it's a teamspace and the user is not an owner and not an admin
                              onClick={(event) => {
                                event.syntheticEvent.stopPropagation()
                                handleExportSupabaseTeamspace(item.recordName)
                              }}
                            >
                              <i
                                className='far fa-file-export mr-2 w-4 text-center'
                                aria-hidden='true'
                              />
                              Export
                            </MenuItem>

                            <MenuItem
                              key='add-member'
                              // Disable 'Manage Members' when it's a teamspace and the user is not an owner and not an admin
                              onClick={(event) => {
                                event.syntheticEvent.stopPropagation()
                                onManageTeamspace(item.recordName, item.title)
                              }}
                            >
                              <i
                                className='far fa-user-plus mr-2 w-4 text-center'
                                aria-hidden='true'
                              />
                              Manage Members
                            </MenuItem>
                          </>
                        ) : undefined}

                        {!item.isOwner && (
                          <MenuItem
                            key='leave-team'
                            // Disable 'Manage Members' when it's a teamspace and the user is not an owner and not an admin
                            onClick={(event) => {
                              event.syntheticEvent.stopPropagation()
                              leaveTeamspace.mutate({
                                teamspaceID: item.recordName,
                              })
                            }}
                          >
                            <i
                              className='far fa-hand-wave mr-2 w-4 text-center'
                              aria-hidden='true'
                            />
                            Leave Teamspace
                          </MenuItem>
                        )}
                      </>
                    )}
                  </Menu>
                )}
            </div>

            {/* The sub-items of this item */}
            {/* Only render when open, but with a wrapper for animation */}
            {isOpen && (
              <div
                className={classNames(
                  'grid transition-[grid-template-rows] duration-300 ease-in-out',
                  'grid-rows-[1fr]'
                )}
                style={{
                  animation: 'slideDown 300ms ease-out'
                }}
              >
                <div className="overflow-hidden">
                  <ul
                    className={classNames(
                      item.header || item.noteType == NoteType.TEAM_SPACE
                        ? 'mt-0 px-0'
                        : 'mt-0 pl-3'
                    )}
                  >
                    {item.isLoading ? (
                      <SkeletonLoader />
                    ) : (
                      item.children.map((subItem) => (
                        <SidebarItem
                          key={subItem.recordName}
                          item={subItem}
                          onManageTeamspace={onManageTeamspace}
                          toggleSidebar={toggleSidebar}
                          isDragging={isDragging}
                        />
                      ))
                    )}
                  </ul>
                </div>
              </div>
            )}
          </div>
        ) : (
          <a
            onClick={(e) => {
              // If the item has an action function, execute it
              // The action should return true if it handled the click and we should stop here
              if (item.action?.()) {
                return
              }

              // IMPORTANT: Menu items (like "+ Create" and "Settings") should not trigger navigation
              // These items have a `menu` property instead of an `action`, and clicking them
              // should only open their dropdown menu, not navigate to a different note
              if (item.menu) {
                return
              }

              if (item.disabled) {
                return
              }

              // For regular sidebar items without menus, navigate to the selected note
              e.preventDefault()
              handleSelectRecordName(item.recordName)
            }}
            onTouchStart={() => {
              // Record the time when touch starts
              if (isMobile) {
                touchStartTime.current = Date.now()
              }
            }}
            onTouchEnd={(event) => {
              // Only handle touch events on mobile
              if (isMobile) {
                // Calculate touch duration
                const touchDuration = Date.now() - touchStartTime.current

                // If it's a short tap (less than 100ms), treat it as a click
                if (touchDuration < 100) {
                  // If this item has a menu, don't close the sidebar or navigate
                  // The menu component will handle the tap event
                  if (item.menu) {
                    return
                  }

                  // If the item has an action, execute it and close the sidebar if it returns true
                  if (item.action?.()) {
                    toggleSidebar()
                    return
                  }

                  if (item.disabled) {
                    return
                  }

                  // For regular items, navigate to the note and close the sidebar
                  event.preventDefault()
                  handleSelectRecordName(item.recordName)
                  toggleSidebar()
                }
              }
            }}
            className={classNames(
              'group cursor-default',
              // 'block rounded-md py-1 text-sm font-regular text-gray-700 dark:text-gray-200 ',
              // item.recordName.startsWith('spacer') && 'h-2.5',
              // !item.disabled && 'hover:bg-stone-200 dark:hover:bg-stone-200/[0.1]',
              item.recordName.startsWith('spacer') ? 'h-2.5' : '',
              // Only apply hover styles when NOT dragging and not disabled
              !item.disabled && !isDragging
                ? 'hover:bg-stone-200 dark:hover:bg-stone-200/[0.1]'
                : '',
              'font-regular ml-2 block rounded-md pl-2 text-base text-gray-700 md:text-sm md:leading-tight dark:text-gray-200',

              item.recordName === selectedRecordName && !isDragging
                ? 'bg-stone-200 dark:bg-stone-200/[0.1]'
                : '',
              // item.menu ? 'pl-0' : 'pl-1.5'
              item.menu ? 'py-0.5' : 'my-0.5 py-1',
              item.menu ? '' : 'pl-6',
              item.action ? 'pl-[0.95em]' : '',
              // Disable pointer events when dragging to prevent hover state issues on non-folder items
              { 'pointer-events-none': isDragging }
            )}
          >
            {item.menu ?? (
              <div className='flex items-center'>
                <span className='truncate'>
                  {/* Add an icon */}
                  <i
                    className={classNames(
                      'mr-2 text-center text-base md:text-[0.9rem] md:leading-tight',
                      iconWidth,
                      item.color,
                      item.icon
                    )}
                  />
                  {item.title}
                </span>
                {item.shortcut ? (
                  <span className='ml-auto mr-2 hidden text-xs font-semibold text-gray-400 md:inline'>
                    {item.shortcut}
                  </span>
                ) : undefined}

                {!item.hideContext && !item.header && !item.disabled && (
                  <Menu
                    align='end'
                    boundingBoxPadding='25px 0px 0px 50px'
                    menuButton={
                      <MenuButton
                        className='m-0 ml-auto p-0 opacity-0 transition-opacity group-hover:opacity-100'
                        onClick={(event) => {
                          event.stopPropagation()
                        }}
                      >
                        <i
                          className='fa-regular fas fa-ellipsis-vertical mx-3 text-base md:text-sm md:leading-tight'
                          aria-hidden='true'
                        />
                      </MenuButton>
                    }
                  >
                    {/* Restore option for items in Archive or Trash */}
                    {item.noteType &&
                      isPrivateNote(item.noteType) &&
                      (item.archiveContext || item.trashContext) && (
                        <MenuItem
                          key='restore'
                          onClick={(event) => {
                            event.syntheticEvent.stopPropagation()
                            void handleRestore()
                          }}
                        >
                          <i
                            className='far fa-undo mr-2 w-4 text-center'
                            aria-hidden='true'
                          />
                          Restore
                        </MenuItem>
                      )}

                    {/* Archive option - only for items NOT in Archive or Trash */}
                    {item.noteType &&
                      isPrivateNote(item.noteType) &&
                      !item.archiveContext &&
                      !item.trashContext && (
                        <MenuItem
                          key='archive'
                          onClick={(event) => {
                            event.syntheticEvent.stopPropagation()
                            const descendenceIds: string[] = traverse(
                              item.children
                            )
                            void handleArchive(descendenceIds)
                          }}
                        >
                          <i
                            className='far fa-archive mr-2 w-4 text-center'
                            aria-hidden='true'
                          />
                          Archive
                        </MenuItem>
                      )}

                    {/* Move to Trash - for items NOT in Trash (but can be in Archive) */}
                    {item.noteType &&
                      isPrivateNote(item.noteType) &&
                      !item.trashContext && (
                        <MenuItem
                          key='move-to-trash'
                          onClick={(event) => {
                            event.syntheticEvent.stopPropagation()
                            const descendenceIds: string[] = traverse(
                              item.children
                            )
                            void handleDelete(descendenceIds)
                          }}
                        >
                          <i
                            className='far fa-trash-can mr-2 w-4 text-center'
                            aria-hidden='true'
                          />
                          Move to Trash
                        </MenuItem>
                      )}

                    {/* Delete Permanently - only for items in Trash */}
                    {item.noteType && item.trashContext && (
                      <MenuItem
                        key='delete-permanently'
                        onClick={(event) => {
                          event.syntheticEvent.stopPropagation()
                          if (
                            !window.confirm(
                              'Are you sure you want to permanently delete this item? This action cannot be undone.'
                            )
                          ) {
                            return
                          }
                          if (item.noteType) {
                            deleteNote.mutate({
                              recordName: item.recordName,
                              noteType: item.noteType,
                            })
                          }
                        }}
                      >
                        <i
                          className='far fa-trash mr-2 w-4 text-center'
                          aria-hidden='true'
                        />
                        Delete Permanently
                      </MenuItem>
                    )}

                    {/* Regular Delete for team notes */}
                    {item.noteType && !isPrivateNote(item.noteType) && (
                      <MenuItem
                        key='delete'
                        onClick={(event) => {
                          event.syntheticEvent.stopPropagation()
                          const descendenceIds: string[] = traverse(
                            item.children
                          )
                          void handleDelete(descendenceIds)
                        }}
                      >
                        <i
                          className='far fa-trash mr-2 w-4 text-center'
                          aria-hidden='true'
                        />
                        Delete
                      </MenuItem>
                    )}
                  </Menu>
                )}
              </div>
            )}
          </a>
        )}
      </li>
    )
  }
)

// This is the draggable version of the SidebarItem
const DraggableSidebarItem = forwardRef<HTMLLIElement, SidebarItemProps>(
  (properties, reference) => {
    const { item } = properties

    // #region drag and drop
    // Only allow dropping on folders, teamspaces, and the main notes section
    const canBeDropTarget =
      Boolean(item.children && item.children.length > 0) ||
      item.noteType === NoteType.TEAM_SPACE ||
      item.recordName === 'notes'

    const { isOver, setNodeRef: setDroppableNodeReference } = useDroppable({
      id: item.recordName,
      disabled:
        (item.disabled ?? (item.header && item.recordName !== 'notes')) ||
        !canBeDropTarget,
      data: {
        noteType: item.noteType,
        filename: item.filename,
        source: item.source,
      },
    })

    // Check if this is a special folder that should not be draggable
    const specialFolders = ['@Templates', '@Archive', '@Trash']
    const isSpecialFolder = item.filename
      ? specialFolders.includes(item.filename)
      : false

    const {
      attributes,
      listeners,
      setNodeRef: setDraggableNodeReference,
    } = useDraggable({
      id: item.recordName,
      disabled:
        Boolean(item.disabled) ||
        Boolean(item.header) ||
        Boolean(item.menu) ||
        isSpecialFolder || // Disable dragging for special folders
        (item.noteType
          ? ![
              NoteType.ASSET_PROJECT_NOTE,
              NoteType.PROJECT_NOTE,
              NoteType.TEAM_SPACE_NOTE,
            ].includes(item.noteType)
          : false),
      data: {
        noteType: item.noteType,
        children: item.children,
        parentRecordName: item.parent,
        parentPath: item.filename?.split('/').slice(0, -1).join('/'),
        source: item.source,
      },
    })

    // https://docs.dndkit.com/api-documentation/sensors/touch#recommendations
    const touchAction = { touchAction: 'manipulation' }
    // #endregion

    // Use the ref forwarding pattern to combine refs
    const setReferences = (node: HTMLLIElement | null) => {
      setDraggableNodeReference(node)
      if (typeof reference === 'function') {
        reference(node)
      } else if (reference) {
        reference.current = node
      }
    }

    // We'll handle the droppable ref in the SidebarItemContent component
    // by passing the setDroppableNodeReference to it

    return (
      <SidebarItemContent
        {...properties}
        ref={setReferences}
        isOver={isOver}
        attributes={attributes}
        listeners={listeners}
        touchAction={touchAction}
        droppableRef={setDroppableNodeReference}
      />
    )
  }
)

// This is the main component that decides whether to use the draggable version or not
export function SidebarItem(
  properties: SidebarItemProps & { isDragOverlay?: boolean }
) {
  // For the drag overlay, we want to use the non-draggable version
  const { isDragOverlay = false } = properties

  if (isDragOverlay) {
    return <SidebarItemContent {...properties} />
  }

  return <DraggableSidebarItem {...properties} />
}

// Add display names for better debugging
SidebarItemContent.displayName = 'SidebarItemContent'
DraggableSidebarItem.displayName = 'DraggableSidebarItem'
